<template>
  <div>
    <div class="font-medium text-xs text-left leading-snug p-4 overflow-auto max-h-[40vh] bg-charcoal-100 dark:bg-dark-950 space-y-4">
      <div class="bg-white dark:bg-dark-800 dark:text-dark-50 p-4 rounded-xl relative  text-light-950 shadow">
        <div class="min-h-24 bg-charcoal-100 dark:bg-dark-600 rounded-lg relative" v-if="formData.amount <= 0">
          <div class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            <IconBase name="cute/spinner" class="spin text-charcoal-400" />
          </div>
        </div>
        <template v-else>
          <div class="min-h-24 bg-charcoal-100 dark:bg-dark-600 text-center text-sm flex flex-col space-y-2 items-center justify-center p-8 rounded-lg relative">
            <IconBase name="cute/spinner" class="spin text-charcoal-400" />

            <template v-if="isOpened">
              <p>{{ $t('components.Event.payment.pending') }}</p>
              <ButtonLink size="xs" @click="emit('previous')">
                {{ $t('actions.cancel') }}
              </ButtonLink>
            </template>
            <template v-else>
              <p>{{ $t('components.Event.payment.loading') }}</p>
              <ButtonLink size="xs" @click="emit('previous')">
                {{ $t('actions.retry') }}
              </ButtonLink>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import EventsStore from '@/store/events'
import { Event, IEvent, Result } from '@/classes'
import { Err } from '@/classes'
import { App } from '@capacitor/app'
import { eventGetOneAPI } from '@/api/event'
import { Browser } from '@capacitor/browser'

const props = defineProps<{
  event: Event
  formData: {
    products: { [key: string]: number }
    amount: number
  }
  isPending?: boolean
}>()

const { event } = toRefs(props)

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'refresh'): void
  (e: 'error', v: Err): void
  (e: 'previous'): void
}>()

const eventStore = EventsStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const isLoading = ref(false)
const isOpened = ref(false)
const interval = ref()

onMounted(async () => {
  try {    
    const isLink = props.formData.amount > 0
    isLoading.value = true

    const data: any = {
      eventId: event.value._id,
      products: props.formData.products,
      isLink
    }
    
    const response: Result<{ event: IEvent, success: boolean, token?: string }> = await fetchAPI('event/checkout', data, { method: 'POST' })

    if (response.ok && response.data.success) {
      if (isLink) {
        Browser.addListener('browserPageLoaded', async () => {
          isOpened.value = true
        })

        if (interval.value) clearInterval(interval.value)
        interval.value = setInterval(async () => {
          const responseEvent = await eventGetOneAPI({ _id: props.event._id})
          
          if (responseEvent.ok && responseEvent.data.event && responseEvent.data.event.attendees.find(u => u._id === currentUser.value?._id && u.type === 'attending')) {
            eventStore.setItem(responseEvent.data.event, { priority: 5 })
            if (interval.value) clearInterval(interval.value)

            Browser.close()

            emit('next')
          }
        }, 2000)

        await Browser.open({ url: useRuntimeConfig().public.PAYMENTS_URL + '/p/' + response.data.token, presentationStyle: 'popover' })
      } else {
        if (response.data.event) eventStore.setItem(response.data.event, { priority: 5 })
        
        await userStore.initUser(true)
      
        emit('next')
      }
    } else {
      console.error(response)
      
      emit('error', { error: 'api-error' } as Err)
      emit('refresh')
      emit('previous')
    }
  } catch (e) {
    console.error(e)

    isLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (interval.value) clearInterval(interval.value)
})
</script>

<style>
.sq-card-message:not(.sq-visible) {
  display: none;
}
</style>
